import _memoize from 'lodash/memoize';
import { getFont, FONT } from '@tornado-rdk/typography';
import { PRIMARY, makeThemeColor, DEFAULT_MODE } from '@tornado-rdk/theme';
import { FULL_HD, HD, safetyMargins, RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import { middle, SCREEN_WIDTH } from '../utils/sizes';
import { cacheResolver, makeDefaultStylingContext } from '../utils/style';
import {
  templates,
  sizes,
  imageOptions,
  INFO_ICON,
  EXTRA_TITLE_FONT,
  EXTRA_TITLE_FONT_TYPE,
  DESCRIPTION_FONT,
  CTA_MARGIN_BOTTOM,
  CTA_BUTTON_MARGIN_RIGHT,
} from './jumbotron.constants';

export { INFO_ICON, LOGO_DIMENSIONS, TRAILER_ICON } from './jumbotron.constants';

export const defaultContext = makeDefaultStylingContext();

export const FLEX_CONTAINER_WIDTH = {
  [HD]: Math.round(SCREEN_WIDTH[HD] * 0.5),
  [FULL_HD]: Math.round(SCREEN_WIDTH[FULL_HD] * 0.45),
};

export const LAYOUT_BOTTOM_MARGIN = {
  [HD]: -64,
  [FULL_HD]: -112,
};

export const BACKGROUND_CONTENT_OFFSET = {
  [HD]: 40,
  [FULL_HD]: 40,
};

export const FLEX_CONTAINER_MARGIN_BOTTOM = {
  [HD]: 104,
  [FULL_HD]: 168,
};

export const LOGO_MARGIN_BOTTOM = {
  [HD]: 8,
  [FULL_HD]: 16,
};

export const INCENTIVE_MARGIN_BOTTOM = {
  [HD]: 8,
  [FULL_HD]: 16,
};

export const SERVICE_PICTO_DIMENSIONS = {
  [HD]: {
    width: 40,
    height: 40,
  },
  [FULL_HD]: {
    width: 56,
    height: 56,
  },
};

export const SERVICE_PICTO_MARGIN_BOTTOM = {
  [HD]: 24,
  [FULL_HD]: 24,
};

export const HIGHLIGHT_MARGIN_BOTTOM = {
  [HD]: 8,
  [FULL_HD]: 16,
};

export const EXTRA_TITLE_MARGIN_BOTTOM = {
  [HD]: 16,
  [FULL_HD]: 24,
};

export const EXTRA_HIGHLIGHT_MARGIN_BOTTOM = {
  [HD]: 8,
  [FULL_HD]: 16,
};

export const EXTRA_HIGHLIGHT_MARGIN_RIGHT = {
  [HD]: 8,
  [FULL_HD]: 16,
};

export const DESCRIPTION_MARGIN_BOTTOM = {
  [HD]: 32,
  [FULL_HD]: 40,
};

export const PROGRESS_MARGIN_BOTTOM = {
  [HD]: 32,
  [FULL_HD]: 32,
};

export const DETAILS_MARGIN_BOTTOM = {
  [HD]: 32,
  [FULL_HD]: 32,
};

export const CONTENT_ADVISORY_ICON_DIMENSION = {
  [HD]: {
    h: 24,
  },
  [FULL_HD]: {
    h: 32,
  },
};

export const CONTENT_ADVISORY_ICON_MARGIN = {
  [HD]: -1,
  [FULL_HD]: -1,
};

export const BASIC_PICTO_DIMENSION = {
  [HD]: 32,
  [FULL_HD]: 40,
};

export const BOTTOM_ROW_GAP = {
  [HD]: 8,
  [FULL_HD]: 8,
};

export const getServiceIconViewBox = _memoize(screenSize => ({
  h: SERVICE_PICTO_DIMENSIONS[screenSize].height,
}));

export const getBasicIconViewBox = _memoize(screenSize => ({
  h: BASIC_PICTO_DIMENSION[screenSize],
}));

export const colors = {
  text: makeThemeColor(PRIMARY),
};

export const makeBasicTextStyle = ({ font, colorMode = DEFAULT_MODE }) => ({
  color: colors.text[colorMode],
  text: {
    ...font,
    verticalAlign: 'middle',
    wordWrap: true,
    maxLines: 2,
    maxLinesSuffix: '...',
  },
});

export const getContentAdvisoryIconStyle = _memoize(
  (picto, screenSize) => ({
    visible: Boolean(picto),
    flexItem: {
      marginRight: CONTENT_ADVISORY_ICON_MARGIN[screenSize],
    },
    iconData: {
      picto,
      viewBox: CONTENT_ADVISORY_ICON_DIMENSION[screenSize],
    },
  }),
  cacheResolver,
);

export const getInitialStyle = _memoize(({ screenSize = FULL_HD, colorMode = DEFAULT_MODE } = defaultContext) => {
  const backgroundSize = imageOptions[templates.Jumbotron][RENDERING_CONTEXT.GENERAL][screenSize].PRIMARY;
  const background = {
    w: backgroundSize.width,
    h: backgroundSize.height,
    x: sizes[templates.Jumbotron][RENDERING_CONTEXT.GENERAL][screenSize].w,
    mountX: 1,
  };

  const { w, h } = sizes[templates.Jumbotron][RENDERING_CONTEXT.GENERAL][screenSize];

  return {
    Background: {
      ...background,
    },
    Gradient: {
      ...background,
    },
    Trailer: {
      ...background,
    },
    FlexContainer: {
      w,
      h: h - FLEX_CONTAINER_MARGIN_BOTTOM[screenSize],
      flex: {
        direction: 'column',
        justifyContent: 'flex-end',
      },
      Logo: {
        flexItem: {
          marginBottom: LOGO_MARGIN_BOTTOM[screenSize],
        },
      },
      Incentive: {
        flexItem: {
          marginBottom: INCENTIVE_MARGIN_BOTTOM[screenSize],
        },
      },
      ServiceIcon: {
        flexItem: {
          marginBottom: SERVICE_PICTO_MARGIN_BOTTOM[screenSize],
        },
      },
      MetadataRow: {
        flex: {
          direction: 'column',
        },
        Highlight: {
          text: {
            ...getFont(FONT.BERLIN_1, screenSize),
            maxLines: 1,
            verticalAlign: 'middle',
          },
          alpha: 0.8,
          flexItem: {
            marginBottom: HIGHLIGHT_MARGIN_BOTTOM[screenSize],
          },
        },
        ExtraTitle: {
          flexItem: {
            marginBottom: EXTRA_TITLE_MARGIN_BOTTOM[screenSize],
          },
          text: {
            ...getFont(EXTRA_TITLE_FONT, screenSize, EXTRA_TITLE_FONT_TYPE),
            maxLines: 2,
            verticalAlign: 'middle',
          },
        },
        ExtraHighlightRow: {
          flex: {
            direction: 'row',
            alignItems: 'center',
          },
          flexItem: {
            marginBottom: EXTRA_HIGHLIGHT_MARGIN_BOTTOM[screenSize],
          },
          ExtraHighlight: {
            flexItem: {
              marginRight: EXTRA_HIGHLIGHT_MARGIN_RIGHT[screenSize],
            },
            h: getFont(FONT.BERLIN_1, screenSize).lineHeight,
            text: {
              ...getFont(FONT.BERLIN_1, screenSize),
              maxLines: 1,
              verticalAlign: 'middle',
            },
            alpha: 0.8,
          },
          ContentAdvisory: {
            flex: {
              direction: 'row',
            },
            IconsRow: {
              h: CONTENT_ADVISORY_ICON_DIMENSION[screenSize].h,
              flex: {
                direction: 'row',
              },
            },
            ContentRatingIcon: {},
          },
        },
        Description: {
          ...makeBasicTextStyle({
            font: getFont(DESCRIPTION_FONT, screenSize),
            colorMode,
          }),
          alpha: 0.8,
          flexItem: {
            marginBottom: DESCRIPTION_MARGIN_BOTTOM[screenSize],
          },
        },
        Details: {
          text: {
            ...getFont(FONT.BERLIN_2, screenSize),
            maxLines: 1,
            verticalAlign: 'middle',
          },
          flexItem: {
            marginBottom: DETAILS_MARGIN_BOTTOM[screenSize],
          },
        },
      },
      ProgressBar: {
        w: w - safetyMargins.horizontal[screenSize] * 2,
        mountY: 1,
        flexItem: {
          marginBottom: PROGRESS_MARGIN_BOTTOM[screenSize],
        },
      },
      ButtonsRow: {
        flex: {
          direction: 'row',
        },
        flexItem: {
          marginBottom: CTA_MARGIN_BOTTOM[screenSize],
        },
        PlayButton: {
          flexItem: {
            marginRight: CTA_BUTTON_MARGIN_RIGHT[screenSize],
          },
        },
        MyListButton: {
          flexItem: {
            marginRight: CTA_BUTTON_MARGIN_RIGHT[screenSize],
          },
        },
        InfoButton: {
          picto: INFO_ICON,
          flexItem: {
            marginRight: CTA_BUTTON_MARGIN_RIGHT[screenSize],
          },
        },
        TrailerButton: {},
      },
      BottomRow: {
        flex: {
          direction: 'row',
          alignItems: 'center',
        },
        ExtraDetails: {
          text: {
            ...getFont(FONT.OTTAWA_2, screenSize),
            maxLines: 1,
            verticalAlign: 'middle',
          },
          flexItem: {
            marginRight: BOTTOM_ROW_GAP[screenSize],
          },
        },
        ContentRatingIcon: {
          mountY: 0.5,
          y: middle,
        },
      },
    },
  };
}, cacheResolver);
